import api, {IdApiArg, PaginatedResult, prepareFTX, SortColumn, UpdateArg, UpdateResp} from "../../api";
import {Tables, TablesInsert, TablesUpdate} from "../../apitypes";
import {supabase} from "../../supabase";

export interface WorkOrderFilter {
    active?: boolean,
    filter?: string,
    number?: string,
    page: number,
    pageSize: number,
    sort?: SortColumn[],
}


export const woApi = api.injectEndpoints({
    endpoints: (build) => ({
        // Machine types
        getWorkOrder: build.query<PaginatedResult<WorkOrderApiGet>, WorkOrderFilter>({
            queryFn: async (args) => {
                let q = supabase
                    .from("workOrder")
                    .select("*,workOrderOrderedProduct(*)", {count: "estimated"})

                if (args.filter) {
                    q = q.textSearch("workorder_ftx", prepareFTX(args.filter))
                }
                if (args.active === true || args.active === false) {
                    q = q.in("status", [
                        "PreparedForProduction",
                        "InProduction",
                        "SendingForProduction",
                        "ApprovedForProduction",
                    ])
                }
                if (args.sort) {
                    args.sort.forEach(s => {
                        q = q.order(s.col, {ascending: s.dir == "asc"})
                    })
                }
                if (args.number) {
                    q = q.eq("number", args.number)
                }
                const {data, count, error} = await q.range(args.pageSize * args.page, args.pageSize * (args.page + 1));

                if (error) return {error};
                return {data: {result: data, count}}
            },
            providesTags: ["WorkOrder"]
        }),
        getWorkOrderById: build.query<Tables<"workOrder"> | null, IdApiArg>({
            queryFn: async (args) => {
                const {data, error} = await supabase
                    .from("workOrder")
                    .select("*,workOrderOrderedProduct(*)")
                    .eq("id", args.id).single()
                if (error) return {error};
                return {data}
            },
            providesTags: ["WorkOrder"]
        }),
        postWorkOrder: build.mutation<UpdateResp, TablesInsert<"workOrder">>({
            queryFn: async (args) => {
                const {error} = await supabase.from("workOrder").insert(args)
                if (error) return {error};
                return {data: {status: "ok"}}
            },
            invalidatesTags: ["WorkOrder"],
        }),
        deleteWorkOrderById: build.mutation<UpdateResp, IdApiArg>({
            queryFn: async (args) => {
                const {error} = await supabase.from("workOrder").delete().eq("id", args.id)
                if (error) return {error};
                return {data: {status: "ok"}}
            },
            invalidatesTags: ["WorkOrder"],
        }),
        putWorkOrderById: build.mutation<UpdateResp, UpdateArg<"workOrder">>({
            queryFn: async (args) => {
                const {error} = await supabase.from("workOrder").update(args.body).eq("id", args.id)
                if (error) return {error};
                return {data: {status: "ok"}}
            },
            invalidatesTags: ["WorkOrder"],
        }),
        getWorkOrderProducts: build.query<Tables<"workOrderOrderedProduct">[], IdApiArg>({
            queryFn: async (args) => {
                const {data, error} = await supabase
                    .from("workOrderOrderedProduct")
                    .select("*")
                    .order("position", {ascending: true})
                    .eq("workOrderID", args.id)
                if (error) return {error};
                return {data}
            },
            providesTags: ["WorkOrderOrderedProduct"]
        }),
        getWorkOrderProductsCount: build.query<any, IdApiArg>({
            queryFn: async (args) => {
                const {data, error} = await supabase
                    .from("workOrderOrderedProduct")
                    .select("id.count()")
                    .eq("workOrderID", args.id)
                    .single()
                if (error) return {error};
                return {data}
            },
            providesTags: ["WorkOrderOrderedProduct"]
        })
    })
});


export type WorkOrderApiGet = Tables<"workOrder"> & {
    workOrderOrderedProduct: Tables<"workOrderOrderedProduct">[] | null
}


export const {
    useGetWorkOrderProductsQuery,
    useGetWorkOrderQuery,
    useGetWorkOrderByIdQuery,
    usePostWorkOrderMutation,
    usePutWorkOrderByIdMutation,
    useDeleteWorkOrderByIdMutation,
useGetWorkOrderProductsCountQuery,
} = woApi;
